"use client";

import {
  defaultLocale,
  isLocale,
  localeCookieName,
  localeToHtmlLang,
  type Locale,
} from "@/lib/i18n/locales";
import { usePathname } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function readInitialLocale(): Locale {
  if (typeof window === "undefined") {
    return defaultLocale;
  }
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${localeCookieName}=([^;]*)`),
  );
  const fromCookie = match?.[1] ? decodeURIComponent(match[1]) : null;
  if (isLocale(fromCookie)) {
    return fromCookie;
  }
  try {
    const fromStorage = window.localStorage.getItem(localeCookieName);
    if (isLocale(fromStorage)) {
      return fromStorage;
    }
  } catch {
    // ignore
  }
  return defaultLocale;
}

function persistLocale(locale: Locale) {
  if (typeof document === "undefined") {
    return;
  }
  const maxAge = `;max-age=${ONE_YEAR_SECONDS}`;
  const path = ";path=/";
  const sameSite = ";SameSite=Lax";
  document.cookie = `${localeCookieName}=${encodeURIComponent(locale)}${path}${maxAge}${sameSite}`;
  try {
    window.localStorage.setItem(localeCookieName, locale);
  } catch {
    // ignore
  }
  document.documentElement.lang = localeToHtmlLang(locale);
}

function localeFromPathname(pathname: string): Locale | null {
  const segment = pathname.split("/").filter(Boolean)[0];
  return isLocale(segment) ? segment : null;
}

export function LocaleProvider({
  children,
  initialLocale = defaultLocale,
}: {
  children: React.ReactNode;
  /** From middleware `x-sl-locale` (root layout) so first paint matches the URL, not `de`. */
  initialLocale?: Locale;
}) {
  const pathname = usePathname();
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale);

  useEffect(() => {
    const fromUrl = localeFromPathname(pathname);
    const initial = fromUrl ?? readInitialLocale();
    setLocaleState(initial);
    document.documentElement.lang = localeToHtmlLang(initial);
    persistLocale(initial);
  }, [pathname]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    persistLocale(next);
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return ctx;
}
